import { useState, useEffect } from "react";
import API from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Table, Plus, Pencil, Trash, Lightning, X } from "@phosphor-icons/react";

function FF({ label, value, onChange, placeholder, testId, mono }) {
  return (
    <div className="space-y-1">
      <Label className="text-[10px] font-semibold uppercase tracking-widest text-zinc-500">{label}</Label>
      <Input data-testid={testId} value={value} onChange={e => onChange(e.target.value)}
        placeholder={placeholder} className={`rounded-sm text-xs h-8 ${mono ? "font-mono" : ""}`} />
    </div>
  );
}

export default function SheetRegistryPage() {
  const [sheets, setSheets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [testing, setTesting] = useState(null);
  const [testResult, setTestResult] = useState(null);
  const [form, setForm] = useState({ name: "", spreadsheet_id: "", sheet_type: "main", tab_name: "", description: "", blocked_tabs_str: "" });

  useEffect(() => { load(); }, []);

  const load = async () => {
    try { const { data } = await API.get("/sheet-registry"); setSheets(data); }
    catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const handleSubmit = async () => {
    try {
      const payload = {
        name: form.name,
        spreadsheet_id: form.spreadsheet_id,
        sheet_type: form.sheet_type,
        tab_name: form.tab_name,
        description: form.description,
        blocked_tabs: (form.blocked_tabs_str || "").split(",").map(s => s.trim()).filter(Boolean),
      };
      if (editing) {
        await API.put(`/sheet-registry/${editing}`, payload);
      } else {
        await API.post("/sheet-registry", payload);
      }
      setShowForm(false); setEditing(null);
      setForm({ name: "", spreadsheet_id: "", sheet_type: "main", tab_name: "", description: "", blocked_tabs_str: "" });
      load();
    } catch (e) { alert("Gagal menyimpan: " + (e.response?.data?.detail || e.message)); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Hapus sheet ini dari registry?")) return;
    try { await API.delete(`/sheet-registry/${id}`); load(); } catch { alert("Delete gagal"); }
  };

  const handleTest = async (id) => {
    setTesting(id); setTestResult(null);
    try {
      const { data } = await API.post(`/sheet-registry/${id}/test-connection`);
      setTestResult(data);
    } catch (e) { setTestResult({ success: false, error: e.message }); }
    finally { setTesting(null); }
  };

  const startEdit = (s) => {
    setEditing(s.id);
    setForm({
      name: s.name,
      spreadsheet_id: s.spreadsheet_id,
      sheet_type: s.sheet_type,
      tab_name: s.tab_name || "",
      description: s.description || "",
      blocked_tabs_str: (s.blocked_tabs || []).join(", "),
    });
    setShowForm(true);
  };

  const startNew = () => {
    setEditing(null);
    setForm({ name: "", spreadsheet_id: "", sheet_type: "main", tab_name: "", description: "", blocked_tabs_str: "" });
    setShowForm(true);
  };

  const TYPE_LABELS = { main: "Rekonsiliasi", tmp: "Tampungan (TMP)", kas: "KAS Perusahaan", other: "Lainnya" };
  const TYPE_COLORS = { main: "bg-blue-50 text-blue-700", tmp: "bg-amber-50 text-amber-700", kas: "bg-emerald-50 text-emerald-700", other: "bg-zinc-100 text-zinc-600" };

  return (
    <div data-testid="sheet-registry-page" className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-lg font-semibold text-zinc-900">Sheet Registry</h1>
          <p className="text-xs text-zinc-500 mt-0.5">Daftar semua Google Sheet yang terdaftar di sistem</p>
        </div>
        <Button data-testid="add-sheet-btn" onClick={startNew} className="rounded-sm gap-2 text-xs h-8">
          <Plus size={14} /> Tambah Sheet
        </Button>
      </div>

      {showForm && (
        <div className="border border-zinc-200 rounded-sm p-4 bg-white space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold">{editing ? "Edit Sheet" : "Tambah Sheet Baru"}</h3>
            <button onClick={() => { setShowForm(false); setEditing(null); }}><X size={16} className="text-zinc-400" /></button>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <FF label="Nama" value={form.name} onChange={v => setForm({...form, name: v})} placeholder="PGRaja Sheet" testId="sheet-name" />
            <FF label="Spreadsheet ID" value={form.spreadsheet_id} onChange={v => setForm({...form, spreadsheet_id: v})} placeholder="1BxiMVs0XRA5nFM..." testId="sheet-sid" mono />
            <div className="space-y-1">
              <Label className="text-[10px] font-semibold uppercase tracking-widest text-zinc-500">Tipe</Label>
              <select data-testid="sheet-type" value={form.sheet_type} onChange={e => setForm({...form, sheet_type: e.target.value})}
                className="w-full border border-zinc-300 rounded-sm px-2 py-1.5 text-xs h-8 outline-none bg-white">
                <option value="main">Rekonsiliasi (Main)</option>
                <option value="tmp">Tampungan (TMP)</option>
                <option value="kas">KAS Perusahaan</option>
                <option value="other">Lainnya</option>
              </select>
            </div>
            <FF label="Tab Utama" value={form.tab_name} onChange={v => setForm({...form, tab_name: v})} placeholder="TMP ALAN" testId="sheet-tab" />
          </div>
          <FF label="Deskripsi" value={form.description} onChange={v => setForm({...form, description: v})} placeholder="Tampungan global untuk semua site" testId="sheet-desc" />
          <div>
            <label className="text-[10px] uppercase tracking-wide text-zinc-500 font-medium">
              Tab Diblokir (Sensitive) <span className="text-[9px] text-zinc-400 normal-case">— pisahkan dengan koma</span>
            </label>
            <input
              data-testid="sheet-blocked-tabs"
              value={form.blocked_tabs_str || ""}
              onChange={e => setForm({...form, blocked_tabs_str: e.target.value})}
              placeholder="contoh: MEDIA, BH, RAHASIA"
              className="w-full border border-red-200 bg-red-50/30 rounded-sm px-2 py-1.5 text-xs h-8 outline-none mt-1"
            />
            <p className="text-[9px] text-red-600 mt-0.5">
              Tab dalam daftar ini TIDAK AKAN PERNAH dibaca oleh sistem maupun AI. Gunakan untuk melindungi data sensitif internal.
              Default hard-block: <span className="font-mono bg-red-100 px-1 rounded">MEDIA</span>, <span className="font-mono bg-red-100 px-1 rounded">BH</span>
            </p>
          </div>
          <div className="flex justify-end gap-2 pt-1">
            <Button variant="outline" onClick={() => { setShowForm(false); setEditing(null); }} className="rounded-sm text-xs h-7">Batal</Button>
            <Button data-testid="save-sheet-btn" onClick={handleSubmit} className="rounded-sm text-xs h-7">{editing ? "Update" : "Simpan"}</Button>
          </div>
        </div>
      )}

      {testResult && (
        <div className={`border rounded-sm p-3 text-xs ${testResult.success ? "border-emerald-300 bg-emerald-50" : "border-red-300 bg-red-50"}`}>
          {testResult.success ? (
            <div>
              <span className="font-semibold text-emerald-700">Koneksi berhasil!</span>
              <span className="ml-2 text-emerald-600">Sheet: {testResult.info?.title} | Tab: {testResult.info?.tabs?.map(t => t.title).join(", ")}</span>
            </div>
          ) : (
            <div><span className="font-semibold text-red-700">Gagal:</span> <span className="text-red-600">{testResult.error}</span></div>
          )}
          <button onClick={() => setTestResult(null)} className="mt-1 text-zinc-400 text-[10px] hover:text-zinc-600">Tutup</button>
        </div>
      )}

      {loading ? <p className="text-xs text-zinc-400">Loading...</p> :
        sheets.length === 0 ? (
          <div className="border border-zinc-200 rounded-sm p-8 text-center text-sm text-zinc-400">Belum ada sheet terdaftar.</div>
        ) : (
          <div className="border border-zinc-200 rounded-sm overflow-hidden">
            <table className="w-full">
              <thead><tr className="bg-zinc-50 border-b border-zinc-200">
                <th className="text-left px-4 py-2 text-[10px] font-semibold uppercase text-zinc-500">Nama</th>
                <th className="text-left px-4 py-2 text-[10px] font-semibold uppercase text-zinc-500">Tipe</th>
                <th className="text-left px-4 py-2 text-[10px] font-semibold uppercase text-zinc-500">Spreadsheet ID</th>
                <th className="text-left px-4 py-2 text-[10px] font-semibold uppercase text-zinc-500">Tab</th>
                <th className="text-left px-4 py-2 text-[10px] font-semibold uppercase text-zinc-500">Deskripsi</th>
                <th className="text-right px-4 py-2 text-[10px] font-semibold uppercase text-zinc-500">Aksi</th>
              </tr></thead>
              <tbody>
                {sheets.map(s => (
                  <tr key={s.id} className="border-b border-zinc-100 hover:bg-zinc-50">
                    <td className="px-4 py-2 text-xs font-semibold text-zinc-900">{s.name}</td>
                    <td className="px-4 py-2">
                      <span className={`text-[9px] font-mono uppercase px-1.5 py-0.5 rounded-sm ${TYPE_COLORS[s.sheet_type] || TYPE_COLORS.other}`}>
                        {TYPE_LABELS[s.sheet_type] || s.sheet_type}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-[10px] font-mono text-zinc-500">{s.spreadsheet_id?.slice(0, 20)}...</td>
                    <td className="px-4 py-2 text-xs text-zinc-600">{s.tab_name || "-"}</td>
                    <td className="px-4 py-2 text-xs text-zinc-400">
                      {s.description || "-"}
                      {(s.blocked_tabs || []).length > 0 && (
                        <div data-testid={`blocked-tabs-${s.id}`} className="mt-1 text-[9px]">
                          <span className="text-red-600 font-semibold">🔒 Blocked:</span>{" "}
                          <span className="font-mono text-red-500">{s.blocked_tabs.join(", ")}</span>
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-2 text-right">
                      <div className="flex justify-end gap-1">
                        <Button variant="outline" size="sm" onClick={() => handleTest(s.id)} disabled={testing === s.id}
                          className="rounded-sm text-[10px] h-6 px-2 gap-1">
                          <Lightning size={11} /> {testing === s.id ? "..." : "Test"}
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => startEdit(s)} className="rounded-sm text-[10px] h-6 px-2 gap-1">
                          <Pencil size={11} /> Edit
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => handleDelete(s.id)} className="rounded-sm text-[10px] h-6 px-2 gap-1 text-red-500 hover:text-red-700">
                          <Trash size={11} />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      }
    </div>
  );
}
